import { useRef, useEffect, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { Layer, MaterialDescriptor, TShirt3DProps, TShirtZone } from '../types'
import {
  drawAlignmentGuides,
  drawSelectionOverlay,
  findTShirtMeshes,
  hitTestOverlayButton,
  LayerDragOverride,
  renderLayersToCanvas,
  toAppUv,
} from '../utils/textureUtils'

const CANVAS_RESOLUTION = 2048
const CANVAS_BACKGROUND = '#ffffff'

const FALLBACK_WHITE_TEXTURE = (() => {
  const data = new Uint8Array([255, 255, 255, 255])
  const texture = new THREE.DataTexture(data, 1, 1)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.needsUpdate = true
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  return texture
})()

type UiPointerEvent = {
  clientX: number
  clientY: number
  preventDefault: () => void
  stopPropagation?: () => void
}

function updateMouseFromEvent(event: PointerEvent, domElement: HTMLElement, mouse: THREE.Vector2) {
  const rect = domElement.getBoundingClientRect()
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
}

function isUvPointInLayer(uv: THREE.Vector2, layer: Layer): boolean {
  const du = uv.x - layer.uvCenter[0]
  const dv = uv.y - layer.uvCenter[1]
  const cos = Math.cos(-layer.uvRotation)
  const sin = Math.sin(-layer.uvRotation)
  const localU = du * cos - dv * sin
  const localV = du * sin + dv * cos
  return Math.abs(localU) <= layer.uvSize[0] / 2 && Math.abs(localV) <= layer.uvSize[1] / 2
}

function pickLayerAtUv(layers: Layer[], uv: THREE.Vector2): Layer | null {
  const sortedLayers = [...layers].sort((a, b) => b.order - a.order)
  return (
    sortedLayers.find(
      layer => !layer.data?.isMotif && !(layer.data as any)?.isSystem && isUvPointInLayer(uv, layer)
    ) || null
  )
}

function normalizeMaterialName(name?: string) {
  return (name || '').trim()
}

function splitMaterialName(name: string) {
  const trimmed = normalizeMaterialName(name)
  if (!trimmed) {
    return { group: 'Groupe', element: 'Element', raw: '' }
  }
  const parts = trimmed.split('/')
  const group = parts[0]?.trim() || trimmed
  const element = parts.slice(1).join('/').trim() || group
  return { group, element, raw: trimmed }
}

function getMaterialKey(material: THREE.Material, meshName: string, materialIndex?: number) {
  const materialName = normalizeMaterialName(material.name)
  if (materialName) return materialName
  const meshLabel = normalizeMaterialName(meshName)
  if (meshLabel) {
    return materialIndex !== undefined ? `${meshLabel}::${materialIndex}` : meshLabel
  }
  return material.uuid
}

const gradientRegex = /^linear-gradient\((\d+)deg,\s*(#[0-9a-fA-F]{6}),\s*(#[0-9a-fA-F]{6})(?:,\s*(\d{1,3})%)?\)$/;

function normalizeColorValue(value: string) {
  const match = value.match(gradientRegex)
  return match ? match[2] : value
}

function parseGradientValue(value: string) {
  const match = value.match(gradientRegex)
  if (!match) return null
  const stop = Math.min(100, Math.max(0, Number(match[4] ?? 50)))
  return {
    angle: Number(match[1]),
    start: match[2],
    end: match[3],
    stop,
  }
}

const gradientTextureCache = new Map<string, THREE.CanvasTexture>()

function getGradientTexture(
  value: string,
  boundsList?: { uMin: number; uMax: number; vMin: number; vMax: number }[]
) {
  const gradient = parseGradientValue(value)
  if (!gradient) return null

  const combined = boundsList && boundsList.length
    ? boundsList.reduce(
        (acc, bounds) => ({
          uMin: Math.min(acc.uMin, bounds.uMin),
          vMin: Math.min(acc.vMin, bounds.vMin),
          uMax: Math.max(acc.uMax, bounds.uMax),
          vMax: Math.max(acc.vMax, bounds.vMax),
        }),
        { uMin: 1, vMin: 1, uMax: 0, vMax: 0 }
      )
    : { uMin: 0, vMin: 0, uMax: 1, vMax: 1 }
  const boundsKey = `${combined.uMin.toFixed(4)},${combined.uMax.toFixed(4)},${combined.vMin.toFixed(4)},${combined.vMax.toFixed(4)}`
  const cacheKey = `${value}|${boundsKey}`
  const cached = gradientTextureCache.get(cacheKey)
  if (cached) return cached

  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const radians = ((gradient.angle - 90) * Math.PI) / 180
  const dx = Math.cos(radians)
  const dy = Math.sin(radians)
  const x0 = size / 2 - dx * (size / 2)
  const y0 = size / 2 - dy * (size / 2)
  const x1 = size / 2 + dx * (size / 2)
  const y1 = size / 2 + dy * (size / 2)
  const paint = ctx.createLinearGradient(x0, y0, x1, y1)
  const offset = Math.min(1, Math.max(-1, (gradient.stop - 50) / 50))
  const stopA = Math.min(1, Math.max(0, 0 + offset))
  const stopB = Math.min(1, Math.max(0, 1 + offset))
  paint.addColorStop(stopA, gradient.start)
  paint.addColorStop(stopB, gradient.end)
  ctx.fillStyle = paint
  ctx.fillRect(0, 0, size, size)

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  texture.generateMipmaps = false
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.anisotropy = 4
  const width = Math.max(0.0001, combined.uMax - combined.uMin)
  const height = Math.max(0.0001, combined.vMax - combined.vMin)
  texture.repeat.set(1 / width, 1 / height)
  texture.offset.set(-combined.uMin / width, -combined.vMin / height)
  texture.needsUpdate = true
  gradientTextureCache.set(cacheKey, texture)
  return texture
}

function isNoPaintMaterialName(name: string) {
  const { group, element, raw } = splitMaterialName(name)
  return raw.toLowerCase() === 'no_paint' || group.toLowerCase() === 'no_paint' || element.toLowerCase() === 'no_paint'
}

function isPaintableMaterial(material: THREE.Material) {
  return !isNoPaintMaterialName(material.name || '')
}

/**
 * Doublure interieure d'un modele tubulaire. Ses normales pointent vers l'axe
 * du tube : aucune lumiere de la scene ne l'atteint, elle ressortait donc NOIRE
 * alors que sa couleur est blanche. On la rend auto-eclairee pour qu'elle soit
 * blanche quelles que soient les lumieres.
 *
 * Cible par son nom ("no_paint/Interieur"), donc sans aucun effet sur le
 * materiau "no_paint" des t-shirts.
 */
function isInnerLiningMaterialName(name: string) {
  const { group, element, raw } = splitMaterialName(name)
  return (
    isNoPaintMaterialName(name) &&
    [raw, group, element].some(part => part.toLowerCase().includes('interieur'))
  )
}

function collectMaterials(root: THREE.Object3D) {
  const all: THREE.Material[] = []
  const paintable: THREE.Material[] = []

  root.traverse(obj => {
    if (!(obj instanceof THREE.Mesh)) return
    const { material } = obj
    if (Array.isArray(material)) {
      material.forEach(item => {
        all.push(item)
        if (isPaintableMaterial(item)) paintable.push(item)
      })
      return
    }
    all.push(material)
    if (isPaintableMaterial(material)) paintable.push(material)
  })

  return { all, paintable }
}

function ensureVisibleMaterial(material: THREE.Material) {
  if ('transparent' in material) {
    material.transparent = false
  }
  if ('opacity' in material) {
    material.opacity = 1
  }
  if ('side' in material) {
    material.side = THREE.DoubleSide
  }
  if ('metalness' in material && typeof material.metalness === 'number') {
    material.metalness = 0
  }
  if ('color' in material && material.color && !(material as THREE.MeshStandardMaterial).map) {
    const color = material.color
    const isNearBlack = color.r < 0.05 && color.g < 0.05 && color.b < 0.05
    if (isNearBlack) {
      color.set('#ffffff')
    }
  }
  if (isInnerLiningMaterialName(material.name || '')) {
    const standard = material as THREE.MeshStandardMaterial
    if (standard.color) standard.color.set('#ffffff')
    if (standard.emissive) {
      // Auto-eclairage : la doublure reste blanche meme sans lumiere incidente.
      standard.emissive.set('#ffffff')
      standard.emissiveIntensity = 1
    }
  }
  material.needsUpdate = true
}

/**
 * Ramene un modele hors gabarit a une taille exploitable par la camera.
 *
 * targetSize vaut la taille des t-shirts (0.695) : un modele redimensionne
 * occupe ainsi le meme cadre qu'eux. C'est le cas du tour de cou, haut de
 * 0.30 m donc sous le seuil, qui etait auparavant porte a 1.4 et s'affichait
 * deux fois plus gros qu'un t-shirt.
 *
 * Les t-shirts, eux, sont dans la bande [minDim, maxDimAllowed] : ils ne
 * passent jamais par cette branche et ne sont donc pas affectes.
 */
function fitModelToView(root: THREE.Object3D, targetSize = 0.7) {
  const box = new THREE.Box3().setFromObject(root)
  const size = new THREE.Vector3()
  box.getSize(size)
  const maxDim = Math.max(size.x, size.y, size.z)
  if (maxDim > 0) {
    const minDim = 0.35
    const maxDimAllowed = 3.5
    if (maxDim < minDim || maxDim > maxDimAllowed) {
      const scale = targetSize / maxDim
      root.scale.multiplyScalar(scale)
    }
  }
  const center = new THREE.Vector3()
  box.setFromObject(root)
  box.getCenter(center)
  root.position.sub(center)
}

function applyMaterialColors(
  root: THREE.Object3D,
  materialColors: Record<string, string>,
  materialBounds?: Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }[]>
) {
  let totalMaterials = 0;
  let mixApplied = 0;
  root.traverse(obj => {
    if (!(obj instanceof THREE.Mesh)) return
    const meshName = obj.name || ''
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
    materials.forEach((material, index) => {
      if (!isPaintableMaterial(material)) return
      totalMaterials += 1;
      if ((material.userData as any)?.designMixApplied) {
        mixApplied += 1;
      }
      const key = getMaterialKey(material, meshName, index)
      const override = materialColors[key]
      if (!override) return
      const bounds = materialBounds?.[key]
      const gradientTexture = getGradientTexture(override, bounds)
      const solidOverride = normalizeColorValue(override)
      const materialData = material.userData as { originalMap?: THREE.Texture | null }
      if (!materialData.originalMap) {
        materialData.originalMap = (material as THREE.MeshStandardMaterial).map || null
      }
      if ('color' in material) {
        material.color.set(gradientTexture ? '#ffffff' : solidOverride)
        material.needsUpdate = true
      }
      if (gradientTexture) {
        material.map = gradientTexture
      } else {
        material.map = materialData.originalMap || FALLBACK_WHITE_TEXTURE
      }
      if (material.map) {
        material.map.needsUpdate = true
      }
    })
  })
  void totalMaterials;
  void mixApplied;
}

function isEditableMesh(object: THREE.Object3D): object is THREE.Mesh {
  if (!(object instanceof THREE.Mesh)) return false
  const { material } = object
  if (Array.isArray(material)) {
    return material.some(item => isPaintableMaterial(item))
  }
  return isPaintableMaterial(material)
}

function applyDesignMix(root: THREE.Object3D, designTexture: THREE.Texture) {
  root.traverse(obj => {
    if (!(obj instanceof THREE.Mesh)) return
    if (obj.name.endsWith('_overlay')) return
    const geometry = obj.geometry as THREE.BufferGeometry
    if (!geometry.getAttribute('uv')) return

    const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
    const nextMaterials = materials.map(original => {
      if (!isPaintableMaterial(original)) return original

      const existing = original as THREE.Material
      const userData = existing.userData as any
      if (userData?.designMixApplied) {
        const uniforms = userData.designMixUniforms as
          | { designMap: { value: THREE.Texture }; printStrength: { value: number } }
          | undefined
        if (uniforms) {
          uniforms.designMap.value = designTexture
        }
        return existing
      }

      const material = existing.clone()
      material.userData = { ...(existing.userData as any) }
      material.onBeforeCompile = shader => {
        shader.uniforms.designMap = { value: designTexture }
        shader.uniforms.printStrength = { value: 1.0 }
        shader.defines.USE_UV = ''
        shader.vertexShader = shader.vertexShader.replace(
          '#include <common>',
          `
          #include <common>
          varying vec2 vDesignUv;
          `
        )
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          `
          #include <uv_vertex>
          #ifdef USE_UV
          vDesignUv = uv;
          #endif
          `
        )
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <common>',
          `
          #include <common>
          uniform sampler2D designMap;
          uniform float printStrength;
          varying vec2 vDesignUv;
          `
        )
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <map_fragment>',
          `
          #include <map_fragment>
          #ifdef USE_UV
          vec4 designColor = texture2D(designMap, vDesignUv);
          float a = clamp(designColor.a * printStrength, 0.0, 1.0);
          vec3 designRgb = designColor.rgb;
          if (designColor.a > 0.0001) {
            designRgb = designColor.rgb / designColor.a;
          }
          diffuseColor.rgb = mix(diffuseColor.rgb, designRgb, a);
          #endif
          `
        )
        ;(material.userData as any).designMixUniforms = {
          designMap: shader.uniforms.designMap,
          printStrength: shader.uniforms.printStrength,
        }
      }
      ;(material.userData as any).designMixApplied = true
      material.needsUpdate = true
      return material
    })

    obj.material = Array.isArray(obj.material) ? nextMaterials : nextMaterials[0]
  })
}

export default function TShirt3D(props: TShirt3DProps) {
  const {
    modelUrl,
    layers,
    selectedLayer,
    materialColors,
    secondaryColor,
    previewRefreshKey,
    allowModelIntroAnimation = true,
    layerFadeOpacities,
    layerFadeNeedsRedraw,
    onLayerSelect,
    onLayerTransform,
    onModelLoad,
    onDragStateChange,
    onMaterialsReady,
    onUvBoundsReady,
    onLayerDuplicate,
    onLayerDelete,
  } = props
  const { camera, gl, scene } = useThree()
  const raycaster = useRef(new THREE.Raycaster())
  const mouse = useRef(new THREE.Vector2())
  const dragging = useRef(false)
  const dragLayerId = useRef<string | null>(null)
  const targetUvRef = useRef<[number, number] | null>(null)
  const guideRef = useRef<{
    u?: number | null;
    v?: number | null;
    bounds?: { uMin: number; uMax: number; vMin: number; vMax: number } | null;
  } | null>(null)
  const guideKeyRef = useRef<string | null>(null)

  const uiDragMode = useRef<'scale' | 'rotate' | null>(null)
  const uiDragLayerId = useRef<string | null>(null)
  // Live transform override during drag — avoids React re-renders on every frame.
  // React state (onLayerTransform) is only called on drop.
  const liveDragOverrideRef = useRef<LayerDragOverride | null>(null)
  const dragStartTapUvRef = useRef<[number, number] | null>(null)
  const uiDragStart = useRef<{ x: number; y: number; uvSize: [number, number]; uvRotation: number }>({
    x: 0,
    y: 0,
    uvSize: [0.1, 0.1],
    uvRotation: 0,
  })

  const ctxRef = useRef<CanvasRenderingContext2D | null>(null)
  const textureRef = useRef<THREE.CanvasTexture | null>(null)
  const [textureReady, setTextureReady] = useState(false)
  const needsRedrawRef = useRef(true)

  const meshRefs = useRef<Record<TShirtZone, THREE.Mesh | null>>({
    front: null,
    back: null,
    leftSleeve: null,
    rightSleeve: null,
  })
  const outsideMeshesRef = useRef<THREE.Mesh[]>([])
  const zoneUvBoundsRef = useRef<Record<TShirtZone, { uMin: number; uMax: number; vMin: number; vMax: number } | null>>({
    front: null,
    back: null,
    leftSleeve: null,
    rightSleeve: null,
  })
  const materialUvBoundsRef = useRef<Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }[]>>({})
  const zoneUvIslandsRef = useRef<Record<TShirtZone, { uMin: number; uMax: number; vMin: number; vMax: number; centerU: number; centerV: number }[]>>({
    front: [],
    back: [],
    leftSleeve: [],
    rightSleeve: [],
  })
  const modelLoadedRef = useRef(false)
  const onModelLoadRef = useRef(onModelLoad)
  const wiggleRef = useRef<{ active: boolean; done: boolean; raf: number | null; timeout: number | null; start: number; startY: number }>({
    active: false,
    done: false,
    raf: null,
    timeout: null,
    start: 0,
    startY: 0,
  })
  const hasAnimatedRef = useRef(false)
  const pendingIntroSpinRef = useRef(false)

  const { scene: gltfScene } = useGLTF(modelUrl)

  useEffect(() => {
    onModelLoadRef.current = onModelLoad
  }, [onModelLoad])

  useEffect(() => {
    modelLoadedRef.current = false
    if (wiggleRef.current.raf !== null) {
      window.cancelAnimationFrame(wiggleRef.current.raf)
    }
    if (wiggleRef.current.timeout !== null) {
      window.clearTimeout(wiggleRef.current.timeout)
    }
    wiggleRef.current = { active: false, done: false, raf: null, timeout: null, start: 0, startY: 0 }
    pendingIntroSpinRef.current = false
    onModelLoadRef.current(false)
  }, [modelUrl])

  useEffect(() => {
    const canvas = document.createElement('canvas')
    canvas.width = CANVAS_RESOLUTION
    canvas.height = CANVAS_RESOLUTION
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) {
      return
    }

    const texture = new THREE.CanvasTexture(canvas)
    texture.flipY = false
    texture.colorSpace = THREE.SRGBColorSpace
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.generateMipmaps = false
    texture.anisotropy = 4
    texture.premultiplyAlpha = false
    texture.needsUpdate = true

    ctxRef.current = ctx
    textureRef.current = texture
    setTextureReady(true)
  }, [])

  useEffect(() => {
    if (!gltfScene || !textureRef.current || !textureReady) return
    applyDesignMix(gltfScene, textureRef.current)
  }, [gltfScene, textureReady])

  useEffect(() => {
    if (!gltfScene || !textureRef.current) return
    applyDesignMix(gltfScene, textureRef.current)
  }, [gltfScene, layers])

  useEffect(() => {
    if (!gltfScene) return
    fitModelToView(gltfScene)

    const meshes = findTShirtMeshes(gltfScene)
    meshRefs.current = meshes
    const materialGroups = collectMaterials(gltfScene)
    const hasNamedColors = materialGroups.paintable.length > 0
    const outsideMeshes: THREE.Mesh[] = []
    gltfScene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return
      if (Array.isArray(obj.material)) {
        obj.material.forEach(item => ensureVisibleMaterial(item))
      } else {
        ensureVisibleMaterial(obj.material)
      }
      if (obj.name.endsWith('_overlay')) {
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
        materials.forEach(material => {
          if ('polygonOffset' in material) {
            material.polygonOffset = true
            material.polygonOffsetFactor = -1
            material.polygonOffsetUnits = -1
          }
          if ('depthWrite' in material) {
            material.depthWrite = false
          }
        })
        obj.renderOrder = 2
      }
      if (hasNamedColors) {
        if (isEditableMesh(obj)) {
          outsideMeshes.push(obj)
        }
        return
      }
      outsideMeshes.push(obj)
    })
    outsideMeshesRef.current = outsideMeshes

    const computeUvIslands = (mesh: THREE.Mesh) => {
      const geometry = mesh.geometry as THREE.BufferGeometry
      const uvAttr = geometry.getAttribute('uv') as THREE.BufferAttribute | null
      if (!uvAttr) return []
      const index = geometry.getIndex()
      const triangleCount = index ? index.count / 3 : uvAttr.count / 3
      if (!index || index.count === 0) {
        let uMin = 1
        let vMin = 1
        let uMax = 0
        let vMax = 0
        for (let i = 0; i < uvAttr.count; i += 1) {
          const u = uvAttr.getX(i)
          const v = uvAttr.getY(i)
          uMin = Math.min(uMin, u)
          vMin = Math.min(vMin, v)
          uMax = Math.max(uMax, u)
          vMax = Math.max(vMax, v)
        }
        return [{ uMin, uMax, vMin, vMax, centerU: (uMin + uMax) / 2, centerV: (vMin + vMax) / 2 }]
      }

      const triangles: [number, number, number][] = []
      for (let i = 0; i < triangleCount; i += 1) {
        const a = index.getX(i * 3)
        const b = index.getX(i * 3 + 1)
        const c = index.getX(i * 3 + 2)
        triangles.push([a, b, c])
      }

      const trisByVertex = new Map<number, number[]>()
      triangles.forEach((tri, triIndex) => {
        tri.forEach(vertexIndex => {
          const list = trisByVertex.get(vertexIndex) || []
          list.push(triIndex)
          trisByVertex.set(vertexIndex, list)
        })
      })

      const visited = new Array(triangles.length).fill(false)
      const islands: { uMin: number; uMax: number; vMin: number; vMax: number; centerU: number; centerV: number }[] = []

      for (let t = 0; t < triangles.length; t += 1) {
        if (visited[t]) continue
        const stack = [t]
        visited[t] = true
        let uMin = 1
        let vMin = 1
        let uMax = 0
        let vMax = 0

        while (stack.length) {
          const triIndex = stack.pop() as number
          const tri = triangles[triIndex]
          tri.forEach(vertexIndex => {
            const u = uvAttr.getX(vertexIndex)
            const v = uvAttr.getY(vertexIndex)
            uMin = Math.min(uMin, u)
            vMin = Math.min(vMin, v)
            uMax = Math.max(uMax, u)
            vMax = Math.max(vMax, v)
            const neighbors = trisByVertex.get(vertexIndex) || []
            neighbors.forEach(neighbor => {
              if (!visited[neighbor]) {
                visited[neighbor] = true
                stack.push(neighbor)
              }
            })
          })
        }

        islands.push({ uMin, uMax, vMin, vMax, centerU: (uMin + uMax) / 2, centerV: (vMin + vMax) / 2 })
      }

      return islands
    }

    const materialBounds: Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }[]> = {}
    const materialTriangles: Record<string, number[][]> = {}

    const recordBounds = (materialKey: string, bounds: { uMin: number; uMax: number; vMin: number; vMax: number }) => {
      if (!materialBounds[materialKey]) {
        materialBounds[materialKey] = []
      }
      materialBounds[materialKey].push(bounds)
    }

    const recordTriangle = (
      materialKey: string,
      u1: number,
      v1: number,
      u2: number,
      v2: number,
      u3: number,
      v3: number
    ) => {
      if (!materialTriangles[materialKey]) {
        materialTriangles[materialKey] = []
      }
      materialTriangles[materialKey].push([u1, v1, u2, v2, u3, v3])
    }

    gltfScene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return
      if (obj.name.endsWith('_overlay')) return
      const geometry = obj.geometry as THREE.BufferGeometry
      const uvAttr = geometry.getAttribute('uv') as THREE.BufferAttribute | null
      if (!uvAttr) return
      const index = geometry.getIndex()
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
      const groups = geometry.groups && geometry.groups.length > 0
        ? geometry.groups
        : [{ start: 0, count: index ? index.count : uvAttr.count, materialIndex: 0 }]

      groups.forEach(group => {
        const material = materials[group.materialIndex] || materials[0]
        if (!material || !isPaintableMaterial(material)) return
        let uMin = 1
        let vMin = 1
        let uMax = 0
        let vMax = 0
        const start = group.start
        const end = group.start + group.count
        const materialKey = getMaterialKey(material, obj.name || '', group.materialIndex)
        if (index) {
          for (let i = start; i < end; i += 1) {
            const vertexIndex = index.getX(i)
            const u = uvAttr.getX(vertexIndex)
            const v = uvAttr.getY(vertexIndex)
            uMin = Math.min(uMin, u)
            vMin = Math.min(vMin, v)
            uMax = Math.max(uMax, u)
            vMax = Math.max(vMax, v)
          }
          for (let i = start; i < end; i += 3) {
            const a = index.getX(i)
            const b = index.getX(i + 1)
            const c = index.getX(i + 2)
            recordTriangle(
              materialKey,
              uvAttr.getX(a),
              uvAttr.getY(a),
              uvAttr.getX(b),
              uvAttr.getY(b),
              uvAttr.getX(c),
              uvAttr.getY(c)
            )
          }
        } else {
          for (let i = start; i < end; i += 1) {
            const u = uvAttr.getX(i)
            const v = uvAttr.getY(i)
            uMin = Math.min(uMin, u)
            vMin = Math.min(vMin, v)
            uMax = Math.max(uMax, u)
            vMax = Math.max(vMax, v)
          }
          for (let i = start; i < end; i += 3) {
            recordTriangle(
              materialKey,
              uvAttr.getX(i),
              uvAttr.getY(i),
              uvAttr.getX(i + 1),
              uvAttr.getY(i + 1),
              uvAttr.getX(i + 2),
              uvAttr.getY(i + 2)
            )
          }
        }
        recordBounds(materialKey, { uMin, uMax, vMin, vMax })
      })
    })

    materialUvBoundsRef.current = materialBounds

    Object.entries(meshes).forEach(([zone, mesh]) => {
      if (!mesh) return
      mesh.geometry.computeVertexNormals()
      mesh.geometry.normalizeNormals()
      const uvAttr = mesh.geometry.getAttribute('uv')
      if (!uvAttr) return
      let uMin = 1
      let vMin = 1
      let uMax = 0
      let vMax = 0
      for (let i = 0; i < uvAttr.count; i += 1) {
        const u = uvAttr.getX(i)
        const v = uvAttr.getY(i)
        uMin = Math.min(uMin, u)
        vMin = Math.min(vMin, v)
        uMax = Math.max(uMax, u)
        vMax = Math.max(vMax, v)
      }
      zoneUvBoundsRef.current[zone as TShirtZone] = { uMin, vMin, uMax, vMax }
      zoneUvIslandsRef.current[zone as TShirtZone] = computeUvIslands(mesh)
    })

    onUvBoundsReady?.(
      { ...materialBounds },
      { ...zoneUvBoundsRef.current },
      { ...materialTriangles }
    )

    if (textureRef.current) {
      applyDesignMix(gltfScene, textureRef.current)
    }

    const hasMesh = Object.values(meshes).some(mesh => mesh !== null)
    const startWiggle = () => {
      if (!gltfScene) return
      if (hasAnimatedRef.current || wiggleRef.current.active || wiggleRef.current.done) return
      const duration = 1500
      const startY = gltfScene.rotation.y || 0
      const ease = (x: number) => x * x * (3 - 2 * x)
      const step = (now: number) => {
        if (!wiggleRef.current.active) return
        const t = Math.min(1, (now - wiggleRef.current.start) / duration)
        let angle = 0
        if (t < 1 / 3) {
          const local = ease(t * 3)
          angle = -20 * local
        } else if (t < 2 / 3) {
          const local = ease((t - 1 / 3) * 3)
          angle = -20 + 40 * local
        } else {
          const local = ease((t - 2 / 3) * 3)
          angle = 20 - 20 * local
        }
        gltfScene.rotation.y = startY + (angle * Math.PI) / 180
        if (t < 1) {
          wiggleRef.current.raf = window.requestAnimationFrame(step)
        } else {
          gltfScene.rotation.y = startY
          wiggleRef.current.active = false
          wiggleRef.current.done = true
          hasAnimatedRef.current = true
          pendingIntroSpinRef.current = false
          wiggleRef.current.raf = null
        }
      }
      wiggleRef.current.active = true
      wiggleRef.current.done = false
      wiggleRef.current.start = performance.now()
      wiggleRef.current.startY = startY
      wiggleRef.current.raf = window.requestAnimationFrame(step)
      wiggleRef.current.timeout = window.setTimeout(() => {
        wiggleRef.current.active = false
        wiggleRef.current.done = true
        hasAnimatedRef.current = true
        pendingIntroSpinRef.current = false
        if (wiggleRef.current.raf !== null) {
          window.cancelAnimationFrame(wiggleRef.current.raf)
          wiggleRef.current.raf = null
        }
        gltfScene.rotation.y = startY
      }, 1600)
    }
    if (hasMesh && !modelLoadedRef.current) {
      modelLoadedRef.current = true
      if (allowModelIntroAnimation) {
        startWiggle()
      } else {
        pendingIntroSpinRef.current = true
      }
      onModelLoadRef.current(true)
    }

    if (allowModelIntroAnimation && pendingIntroSpinRef.current && modelLoadedRef.current) {
      startWiggle()
    }
  }, [gltfScene, allowModelIntroAnimation])

  useEffect(() => {
    if (!gltfScene) return
    applyMaterialColors(gltfScene, materialColors, materialUvBoundsRef.current)
    if (textureRef.current) {
      applyDesignMix(gltfScene, textureRef.current)
    }
  }, [gltfScene, materialColors])

  useEffect(() => {
    if (!gltfScene || !onMaterialsReady) return
    const descriptorMap = new Map<string, MaterialDescriptor>()

    gltfScene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return
      if (obj.name.endsWith('_overlay')) return
      const meshName = obj.name || ''
      const { material } = obj
      const materials = Array.isArray(material) ? material : [material]
      materials.forEach((item, index) => {
        if (!isPaintableMaterial(item)) return
        const materialKey = getMaterialKey(item, meshName, index)
        if (descriptorMap.has(materialKey)) return
        const candidateName = normalizeMaterialName(item.name) || normalizeMaterialName(meshName)
        const info = splitMaterialName(candidateName)
        const defaultColor =
          'color' in item && item.color
            ? `#${item.color.getHexString()}`
            : '#ffffff'
        descriptorMap.set(materialKey, {
          id: materialKey,
          name: info.raw || candidateName || 'Materiau',
          group: info.group,
          element: info.element,
          defaultColor,
        })
      })
    })

    const descriptors = Array.from(descriptorMap.values())
    onMaterialsReady(descriptors)
  }, [gltfScene, onMaterialsReady])

  useEffect(() => {
    needsRedrawRef.current = true
  }, [layers, selectedLayer, secondaryColor])

  useEffect(() => {
    if (previewRefreshKey === undefined) return
    needsRedrawRef.current = true
    if (gltfScene && textureRef.current) {
      applyDesignMix(gltfScene, textureRef.current)
    }
  }, [previewRefreshKey, gltfScene])

  useFrame(() => {
    if (layerFadeNeedsRedraw?.current) {
      layerFadeNeedsRedraw.current = false
      needsRedrawRef.current = true
    }
    if (!needsRedrawRef.current || !ctxRef.current || !textureRef.current) return

    const liveOverride = liveDragOverrideRef.current

    renderLayersToCanvas(
      ctxRef.current,
      layers,
      CANVAS_RESOLUTION,
      CANVAS_RESOLUTION,
      undefined,
      zoneUvBoundsRef.current as Record<TShirtZone, { uMin: number; uMax: number; vMin: number; vMax: number }>,
      secondaryColor,
      materialUvBoundsRef.current,
      undefined,
      liveOverride,
      layerFadeOpacities?.current
    )

    if (guideRef.current) {
      drawAlignmentGuides(ctxRef.current, CANVAS_RESOLUTION, CANVAS_RESOLUTION, guideRef.current)
    }

    if (selectedLayer) {
      // Apply live override to the selection overlay so handles follow the drag
      const overlayLayer =
        liveOverride && selectedLayer.id === liveOverride.id
          ? {
              ...selectedLayer,
              uvCenter: liveOverride.uvCenter ?? selectedLayer.uvCenter,
              uvSize: liveOverride.uvSize ?? selectedLayer.uvSize,
              uvRotation: liveOverride.uvRotation ?? selectedLayer.uvRotation,
            }
          : selectedLayer
      const iconsReady = drawSelectionOverlay(
        ctxRef.current,
        overlayLayer,
        CANVAS_RESOLUTION,
        CANVAS_RESOLUTION
      )
      if (!iconsReady) {
        needsRedrawRef.current = true
      }
    }

    textureRef.current.needsUpdate = true
    needsRedrawRef.current = false
  })


  useFrame(() => {
    if (!dragging.current || !dragLayerId.current) {
      if (guideRef.current) {
        guideRef.current = null
        needsRedrawRef.current = true
      }
      return
    }

    raycaster.current.setFromCamera(mouse.current, camera)
    const intersects = raycaster.current.intersectObjects(outsideMeshesRef.current, true)
    const tshirtIntersects = intersects.filter(intersect => isEditableMesh(intersect.object))

    if (tshirtIntersects.length === 0) return
    const hit = tshirtIntersects[0]
    if (!hit.uv) return

    const newUv: [number, number] = toAppUv(hit.uv)
    const dragLayer = layers.find(layer => layer.id === dragLayerId.current)
    const isGlobalUv = Boolean((dragLayer?.data as any)?.isGlobalUv)
    const zone = isGlobalUv ? null : (dragLayer?.zone ?? null)
    const islands = zone ? zoneUvIslandsRef.current[zone] : []
    const zoneBounds = zone ? zoneUvBoundsRef.current[zone] : null
    const pickedIsland =
      islands.find(island =>
        newUv[0] >= island.uMin &&
        newUv[0] <= island.uMax &&
        newUv[1] >= island.vMin &&
        newUv[1] <= island.vMax
      ) ||
      (islands.length
        ? islands.reduce((best, island) => {
            const bestDist =
              Math.abs(best.centerU - newUv[0]) + Math.abs(best.centerV - newUv[1])
            const nextDist =
              Math.abs(island.centerU - newUv[0]) + Math.abs(island.centerV - newUv[1])
            return nextDist < bestDist ? island : best
          }, islands[0])
        : null)

    const activeBounds = pickedIsland || zoneBounds
    const centerU = activeBounds ? (activeBounds.uMin + activeBounds.uMax) / 2 : 0.5
    const centerV = activeBounds ? (activeBounds.vMin + activeBounds.vMax) / 2 : 0.5
    const thresholdU = activeBounds ? Math.max(0.006, (activeBounds.uMax - activeBounds.uMin) * 0.04) : 0.02
    const thresholdV = activeBounds ? Math.max(0.006, (activeBounds.vMax - activeBounds.vMin) * 0.04) : 0.02

    let snappedU = newUv[0]
    let snappedV = newUv[1]
    let showU = false
    let showV = false
    if (Math.abs(newUv[0] - centerU) <= thresholdU) {
      snappedU = centerU
      showU = true
    }
    if (Math.abs(newUv[1] - centerV) <= thresholdV) {
      snappedV = centerV
      showV = true
    }

    const nextGuide = showU || showV ? { u: showU ? centerU : null, v: showV ? centerV : null, bounds: activeBounds || null } : null
    const nextGuideKey = nextGuide
      ? `${nextGuide.bounds ? 'bounds' : 'all'}:${nextGuide.u ?? ''}:${nextGuide.v ?? ''}`
      : null
    if (nextGuideKey !== guideKeyRef.current) {
      guideKeyRef.current = nextGuideKey
      needsRedrawRef.current = true
    }
    guideRef.current = nextGuide
    targetUvRef.current = [snappedU, snappedV]

    // Only start moving once the tap has moved beyond a small threshold (avoids
    // snapping the layer to the tap point on a plain click without drag).
    const startTap = dragStartTapUvRef.current
    const hasMoved = startTap
      ? Math.abs(newUv[0] - startTap[0]) + Math.abs(newUv[1] - startTap[1]) > 0.008
      : true
    if (hasMoved) {
      liveDragOverrideRef.current = { id: dragLayerId.current, uvCenter: [snappedU, snappedV] }
    } else {
      liveDragOverrideRef.current = null
    }
    needsRedrawRef.current = true
  })

  const handleUiPointerMove = (event: PointerEvent) => {
    const layerId = uiDragLayerId.current
    if (!layerId) return
    const mode = uiDragMode.current
    if (!mode) return

    const dx = event.clientX - uiDragStart.current.x
    const dy = event.clientY - uiDragStart.current.y

    if (mode === 'scale') {
      const delta = (dx - dy) * 0.005
      const factor = Math.max(0.1, 1 + delta)
      const newSize: [number, number] = [
        Math.max(0.02, uiDragStart.current.uvSize[0] * factor),
        Math.max(0.02, uiDragStart.current.uvSize[1] * factor),
      ]
      liveDragOverrideRef.current = { id: layerId, uvSize: newSize }
      needsRedrawRef.current = true
      return
    }

    const newRotation = uiDragStart.current.uvRotation + dx * 0.01
    liveDragOverrideRef.current = { id: layerId, uvRotation: newRotation }
    needsRedrawRef.current = true
  }

  const handleUiPointerUp = () => {
    // Commit final scale/rotation to React state (once, on release)
    const finalOverride = liveDragOverrideRef.current
    if (finalOverride?.id) {
      const update: Partial<{ uvSize: [number, number]; uvRotation: number }> = {}
      if (finalOverride.uvSize) update.uvSize = finalOverride.uvSize
      if (finalOverride.uvRotation !== undefined) update.uvRotation = finalOverride.uvRotation
      if (Object.keys(update).length > 0) onLayerTransform(finalOverride.id, update)
    }
    liveDragOverrideRef.current = null
    uiDragMode.current = null
    uiDragLayerId.current = null
    window.removeEventListener('pointermove', handleUiPointerMove)
    window.removeEventListener('pointerup', handleUiPointerUp)
    onDragStateChange(false)
  }

  const startUiDrag = (mode: 'scale' | 'rotate', event: UiPointerEvent) => {
    if (!selectedLayer || selectedLayer.locked) return
    event.preventDefault()
    if (event.stopPropagation) event.stopPropagation()
    uiDragMode.current = mode
    uiDragLayerId.current = selectedLayer.id
    uiDragStart.current = {
      x: event.clientX,
      y: event.clientY,
      uvSize: [...selectedLayer.uvSize] as [number, number],
      uvRotation: selectedLayer.uvRotation,
    }
    window.addEventListener('pointermove', handleUiPointerMove)
    window.addEventListener('pointerup', handleUiPointerUp)
    onDragStateChange(true)
  }

  const handleDuplicateLayer = () => {
    if (selectedLayer && onLayerDuplicate) {
      onLayerDuplicate(selectedLayer.id)
      needsRedrawRef.current = true
    }
  }

  const handleDeleteLayer = () => {
    if (selectedLayer && onLayerDelete) {
      onLayerDelete(selectedLayer.id)
    }
  }

  const handleToggleLock = () => {
    if (!selectedLayer) return
    onLayerTransform(selectedLayer.id, { locked: !selectedLayer.locked })
  }

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      updateMouseFromEvent(event, gl.domElement, mouse.current)

      raycaster.current.setFromCamera(mouse.current, camera)
      const intersects = raycaster.current.intersectObjects(outsideMeshesRef.current, true)
      const tshirtIntersects = intersects.filter(intersect => isEditableMesh(intersect.object))

      if (tshirtIntersects.length === 0) return
      const hit = tshirtIntersects[0]
      if (!hit.uv) return

      if (selectedLayer && !selectedLayer.data?.isMotif) {
        const hitUv = toAppUv(hit.uv)
        const button = hitTestOverlayButton(
          hitUv,
          selectedLayer,
          CANVAS_RESOLUTION,
          CANVAS_RESOLUTION
        )
        if (button) {
          onLayerSelect(selectedLayer.id)
          if (button === 'pin') {
            handleToggleLock()
            return
          }
          if (button === 'duplicate') {
            handleDuplicateLayer()
            return
          }
          if (button === 'delete') {
            handleDeleteLayer()
            return
          }
          if (selectedLayer.locked) return
          if (button === 'rotate') {
            startUiDrag('rotate', event)
            return
          }
          if (button === 'scale') {
            startUiDrag('scale', event)
            return
          }
        }
      }

      const pickedLayer = pickLayerAtUv(layers, new THREE.Vector2(...toAppUv(hit.uv)))
      if (!pickedLayer) {
        onLayerSelect(null)
        return
      }

      if (pickedLayer.data?.isMotif) {
        onLayerSelect(null)
        return
      }

      onLayerSelect(pickedLayer.id)
      if (pickedLayer.locked) return

      dragging.current = true
      dragLayerId.current = pickedLayer.id
      dragStartTapUvRef.current = [...toAppUv(hit.uv)] as [number, number]
      liveDragOverrideRef.current = null
      onDragStateChange(true)
      gl.domElement.setPointerCapture(event.pointerId)
      event.preventDefault()
    }

    const handlePointerMove = (event: PointerEvent) => {
      updateMouseFromEvent(event, gl.domElement, mouse.current)
    }

    const handlePointerUp = (event: PointerEvent) => {
      // Ignore pointerup events not coming from translate-drag.
      // Scale/rotate UI drags use a dedicated window pointerup handler to commit changes.
      if (!dragging.current) {
        return
      }
      // Commit final position to React state (once, on release)
      const finalOverride = liveDragOverrideRef.current
      if (finalOverride?.id && finalOverride.uvCenter) {
        onLayerTransform(finalOverride.id, { uvCenter: finalOverride.uvCenter })
      }
      liveDragOverrideRef.current = null
      dragging.current = false
      dragLayerId.current = null
      targetUvRef.current = null
      guideRef.current = null
      guideKeyRef.current = null
      onDragStateChange(false)
      if (gl.domElement.hasPointerCapture(event.pointerId)) {
        gl.domElement.releasePointerCapture(event.pointerId)
      }
    }

    gl.domElement.addEventListener('pointerdown', handlePointerDown)
    gl.domElement.addEventListener('pointermove', handlePointerMove)
    gl.domElement.addEventListener('pointerup', handlePointerUp)

    return () => {
      gl.domElement.removeEventListener('pointerdown', handlePointerDown)
      gl.domElement.removeEventListener('pointermove', handlePointerMove)
      gl.domElement.removeEventListener('pointerup', handlePointerUp)
    }
  }, [camera, gl.domElement, layers, onDragStateChange, onLayerSelect, scene, selectedLayer])

  return (
    <group>
      {gltfScene && <primitive object={gltfScene} />}
    </group>
  )
}

useGLTF.preload('/model/Tshirt%20Homme%20Manches%20Courtes.glb')
